import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import serverUrl from '../_mock/account';
import {
  getRefresh,
  storeToken,
  removeToken,
  storeSession,
  setLastActivity,
  getLastActivity
} from './LocalStorageService';

const baseUrl = serverUrl.serverUrl;
const apiEndpoint = '/api/';
const url = baseUrl + apiEndpoint;
const TOKEN_VALIDITY_PERIOD = 1 * 60 * 1000; 

const customBaseQuery = async (args, api, extraOptions) => {
  const rawBaseQuery = fetchBaseQuery({ baseUrl: url });
  const now = Date.now();
  const lastActivity = getLastActivity();
  const result = await rawBaseQuery(args, api, extraOptions);
  const errorResponse = result?.error?.data;
  if (
    errorResponse?.detail === "Given token not valid for any token type" &&
    errorResponse?.code === "token_not_valid" || 
    errorResponse?.detail === "Authorization header must contain two space-delimited values" &&
    errorResponse?.code === "bad_authorization_header"
  ) {
    removeToken();
    storeSession(true);
    window.location.href = '/';
    return result;
  }

  if (lastActivity && now - lastActivity >= TOKEN_VALIDITY_PERIOD) {
    setLastActivity(now);
    try {
      const data = await fetch(`${url}authentication/user/token/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: getRefresh() }),
      });
      const refreshData = await data.json();
      const newAccessToken = refreshData.access_token;

      if (newAccessToken) {
        storeToken(newAccessToken);
        setLastActivity(Date.now());
      } else {
        removeToken();
        storeSession(true);
        window.location.href = '/';
      }
    } catch (err) {
      removeToken();
      storeSession(true);
      window.location.href = '/';
    }
  }
  return result;
};


export const dvagent = createApi({
    reducerPath: 'dvagent',
    baseQuery: customBaseQuery,
      endpoints: (builder) => ({
        addUser: builder.mutation({
          query: ({token, data}) => ({
              url: 'authentication/add/user',
              method: 'POST',
              body: data,
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        updateUser: builder.mutation({
          query: ({token, data}) => ({
              url: 'authentication/update/user',
              method: 'PUT',
              body: data,
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        deleteUser: builder.mutation({
          query: ({token, data}) => ({
              url: 'authentication/delete/user',
              method: 'DELETE',
              body: data,
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        changePassword: builder.mutation({
          query: ({token, data}) => ({
              url: 'authentication/user/change-password',
              method: 'POST',
              body: data,
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        resetPassword: builder.mutation({
          query: ({token, data}) => ({
              url: 'authentication/user/reset-password',
              method: 'POST',
              body: data,
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        login: builder.mutation({
          query: (user) => ({
              url: 'authentication/user/login',
              method: 'POST',
              body: user,
              headers: {
                'Content-type': 'application/json',
              }
            })
        }),
        requestResetPassword: builder.mutation({
          query: (data) => ({
              url: 'authentication/user/request-password',
              method: 'POST',
              body: data,
              headers: {
                'Content-type': 'application/json',
              }
            })
        }),
        userView: builder.query({
          query: ({token}) => ({
              url: 'authentication/user/view',
              method: 'GET',
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        userProfileData: builder.query({
          query: ({email, token}) => ({
              url: `authentication/search/user?email=${email}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        userViewProperty: builder.query({
          query: ({token}) => ({
              url: 'authentication/user/property',
              method: 'GET',
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        networkTagList: builder.query({
          query: ({key, network, token}) => ({
              url: `authentication/search/user?network-id==${network}&API-Key=${key}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        userProfile: builder.query({
          query: (token) => ({
              url: 'authentication/user/profile',
              method: 'GET',
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        organisationId: builder.mutation({
          query: ({ apiKey, token }) => ({
              url: `information/get/organisation?API-Key=${apiKey}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        networks: builder.mutation({
          query: ({ apiKey, organisationId, token }) => ({
              url: `information/get/networks?API-Key=${apiKey}&organisation-id=${organisationId}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        networksList: builder.query({
          query: ({ apiKey, organisationId, token }) => ({
              url: `information/get/networks?API-Key=${apiKey}&organisation-id=${organisationId}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        addProperty: builder.mutation({
          query: ({token, data}) => ({
              url: 'information/add/property',
              method: 'POST',
              body: data,
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        propertyView: builder.query({
          query: ({id, token}) => ({
              url: `information/search/property?id=${id}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        getDevices: builder.query({
          query: ({ propertyId, networkId, token }) => ({
              url: `/information/view/device?property_id=${propertyId}&network_id=${networkId}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        viewProperty: builder.query({
          query: ({token}) => ({
              url: '/information/view/property',
              method: 'GET',
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        updateProperty: builder.mutation({
          query: ({token, data}) => ({
              url: 'information/update/property',
              method: 'PUT',
              body: data,
              headers: {
                // 'Content-Type':'multipart/form-data;',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        userAccess: builder.mutation({
          query: ({ name, networkId, token}) => ({
              url: `information/user/permission?name=${encodeURIComponent(name)}&network_id=${networkId}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        userAccessPermission: builder.query({
          query: ({token}) => ({
              url: `information/user/permission`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        userNetwork: builder.mutation({
          query: ({ id, token}) => ({
              url: `/authentication/user/network?id=${encodeURIComponent(id)}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        updateUserAccess: builder.mutation({
          query: ({token, data}) => ({
              url: 'information/update/user/permission',
              method: 'POST',
              body: data,
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        deleteProprty: builder.mutation({
          query: ({token, data}) => ({
              url: 'information/remove/property',
              method: 'DELETE',
              body: data,
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        deviceProfile: builder.query({
          query: ({ name, propertyId, networkId, deviceId, serial, organisationId, token}) => ({
              url: `information/view/device/profile?property_id=${propertyId}&device-id=${deviceId}&network-id=${networkId}&serial=${serial}&organisation-id=${organisationId}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        singleAppMode: builder.mutation({
          query: ({token, propertyId, networkId, data, message}) => ({
              url: `information/device/change/mode?property_id=${propertyId}&network-id=${networkId}&message=${message}`,
              method: 'POST',
              body: data,
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        deviceNetworkChange: builder.mutation({
          query: ({token, networkId, propertyId, data, message}) => ({
              url: `information/device/change/network?network_id=${networkId}&message=${message}&property_id=${propertyId}`,
              method: 'POST',
              body: data,
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        changeDeviceName: builder.mutation({
          query: ({token, networkId,propertyId, serial, name}) => ({
              url: `information/device/edit/name?property_id=${propertyId}&network-id=${networkId}&serial=${serial}&name=${encodeURIComponent(name)}`,
              method: 'PUT',
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        refreshDeviceDetail: builder.mutation({
          query: ({deviceId, networkId, propertyId, token}) => ({
              url: `information/device/refresh?property_id=${propertyId}&network-id=${networkId}&device-id=${deviceId}`,
              method: 'POST',
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        refreshDeviceProfile: builder.mutation({
          query: ({ name, propertyId, networkId, deviceId, serial, organisationId, token}) => ({
              url: `information/view/device/profile?property_id=${propertyId}&device-id=${deviceId}&network-id=${networkId}&serial=${serial}&organisation-id=${organisationId}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        activity: builder.query({
          query: ({ email, networkId, token}) => ({
              url: `authentication/activity?email=${email}&network-id=${networkId}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        deviceSearch: builder.mutation({
          query: ({ name, propertyId, serial, networkId, token}) => ({
              url: `information/device/search?property_id=${propertyId}&name=${encodeURIComponent(name)}&current-network-id=${networkId}&serial=${serial}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        replaceDevice: builder.mutation({
          query: ({token, data}) => ({
              url: `information/device/replace`,
              method: 'POST',
              body: data,
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        appUpdate: builder.mutation({
          query: ({token, data}) => ({
              url: `information/device/app/update`,
              method: 'POST',
              body: data,
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        syncApp: builder.mutation({
          query: ({token, data}) => ({
              url: `information/sync/app`,
              method: 'POST',
              body: data,
              headers: {
                // 'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        syncTags: builder.mutation({
          query: ({token, data}) => ({
              url: `information/sync/tags`,
              method: 'POST',
              body: data,
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        appVersion: builder.query({
          query: ({ name,propertyId, token}) => ({
              url: `information/appversion?property_id=${propertyId}&property_name=${encodeURIComponent(name)}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        updateProcess: builder.mutation({
          query: ({ networkId, id, token}) => ({
              url: `information/device/app/update/process?networkId=${networkId}&id=${id}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),

        getPermission: builder.query({
          query: ({ token }) => ({
              url: `authentication/features`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        addPolicy: builder.mutation({
          query: ({ token, data }) => ({
              url: `authentication/policy/add`,
              method: 'POST',
              body: data,
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        getPolicy: builder.query({
          query: ({ token }) => ({
              url: `authentication/policies`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        getOnePolicy: builder.query({
          query: ({ token, id }) => ({
              url: `authentication/policy/view?id=${id}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        updatePolicy: builder.mutation({
          query: ({token, data}) => ({
              url: `authentication/policy/edit`,
              method: 'PUT',
              body: data,
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        deletePolicy: builder.mutation({
          query: ({token, id}) => ({
              url: `authentication/policy/delete?id=${id}`,
              method: 'DELETE',
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        addGroup: builder.mutation({
          query: ({ token, data }) => ({
              url: `authentication/group/add`,
              method: 'POST',
              body: data,
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        getGroup: builder.query({
          query: ({ token }) => ({
              url: `authentication/groups`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        getOneGroup: builder.query({
          query: ({ token, id }) => ({
              url: `authentication/group/view?id=${id}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        updateGroup: builder.mutation({
          query: ({token, data}) => ({
              url: `authentication/group/edit`,
              method: 'PUT',
              body: data,
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        deleteGroup: builder.mutation({
          query: ({token, id}) => ({
              url: `authentication/group/delete?id=${id}`,
              method: 'DELETE',
              headers: {
                'Content-type': 'application/json',
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        getUserAccess: builder.query({
          query: ({ token, id }) => ({
              url: `authentication/user-access?id=${id}`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
        getAllUserAccess: builder.query({
          query: ({ token }) => ({
              url: `authentication/user-access`,
              method: 'GET',
              headers: {
                'authorization': `Bearer ${token}`,
              }
            })
        }),
      }),
})

export const {  
                useLoginMutation,

                useOrganisationIdMutation, 
                useNetworksMutation, 
                useNetworkTagListQuery,

                useAddPropertyMutation,
                useViewPropertyQuery,
                usePropertyViewQuery,
                useDeleteProprtyMutation,
                useUpdatePropertyMutation,
                useSyncAppMutation,
                useAppVersionQuery,

                useAddUserMutation,
                useUpdateUserMutation,
                useDeleteUserMutation,
                useUserProfileDataQuery,
                useUserViewPropertyQuery, 
                useUserProfileQuery, 
                useUserViewQuery,
                useUserAccessMutation,
                useUserAccessPermissionQuery,
                useUpdateUserAccessMutation,
                useUserNetworkMutation,
                useChangePasswordMutation,
                useResetPasswordMutation,
                useRequestResetPasswordMutation,

                useGetDevicesQuery,
                useDeviceProfileQuery,
                useSingleAppModeMutation,
                useDeviceNetworkChangeMutation,
                useChangeDeviceNameMutation,
                useRefreshDeviceDetailMutation,
                useRefreshDeviceProfileMutation,
                useActivityQuery,
                useDeviceSearchMutation,
                useReplaceDeviceMutation,
                useNetworksListQuery,
                useAppUpdateMutation,
                useUpdateProcessMutation,
                useSyncTagsMutation,

                useGetPermissionQuery,
                useGetUserAccessQuery,
                useGetAllUserAccessQuery,
                
                useAddPolicyMutation,
                useGetPolicyQuery,
                useGetOnePolicyQuery,
                useUpdatePolicyMutation,
                useDeletePolicyMutation,

                useAddGroupMutation,
                useGetGroupQuery,
                useGetOneGroupQuery,
                useUpdateGroupMutation,
                useDeleteGroupMutation
              } = dvagent